import React from 'react';
import { useTranslation } from 'react-i18next';
import { AgeValue, OptionKeyValue, ProstatePathology } from '@myriadgenetics/mgh-types';
import get from 'lodash.get';

import { Text3 } from '../../../elements/typography';

import './index.scss';

export enum YesNo {
  Yes = 'yes',
  No = 'no',
}

const SEPARATOR = ':';
const yesNoMapper = (val: boolean, noKey?: string) => (val ? YesNo.Yes : noKey || YesNo.No);

type DetailProps = {
  testId?: string;
  displayKey: string | number | OptionKeyValue[];
  labelKey: string;
};

function ProstateCancerDetail({ testId, displayKey, labelKey }: DetailProps) {
  const { t } = useTranslation();
  return (
    <p data-testid={testId} className={`prostate-cancer-details__detail`}>
      <Text3 className="prostate-cancer-details__detail-label text-medium--regular" tag="span">
        {`${t(labelKey)}${SEPARATOR}`}
      </Text3>
      <Text3 tag="span" className="text-medium--semi-bold">
        {typeof displayKey === 'string' || typeof displayKey === 'number' ? t(String(displayKey)) : t(displayKey[0].optionValue)}
      </Text3>
    </p>
  );
}

interface Props {
  pathology: ProstatePathology;
}

function ProstateCancerDetails({ pathology }: Props) {
  const isAgeRange = (age: AgeValue | undefined) => Array.isArray(age);

  return (
    <div data-testid="prostate-cancer-details" className="prostate-cancer-details">
      {pathology.age !== undefined && (
        <ProstateCancerDetail
          testId="prostate-cancer-details"
          labelKey="review.ageOfDiagnosis"
          displayKey={
            isAgeRange(get(pathology, 'age')) ? get(pathology, 'age[0].optionValue', 'pathology.dontKnow') : get(pathology, 'age', 'pathology.dontKnow')
          }
        />
      )}
      {pathology.prostate?.metastatic !== undefined && (
        <ProstateCancerDetail
          testId="prostate-cancer-details-metastatic"
          labelKey="review.metastatic"
          displayKey={yesNoMapper(get(pathology, 'prostate.metastatic', false), 'pathology.noOrUnknown')}
        />
      )}
      {pathology.prostate?.gleasonScore !== undefined && (
        <ProstateCancerDetail
          testId="prostate-cancer-details-gleason-score"
          labelKey="review.gleasonScore"
          displayKey={get(pathology, 'prostate.gleasonScore[0].optionValue', 'pathology.dontKnow')}
        />
      )}
    </div>
  );
}

export default ProstateCancerDetails;
